// Phase 2.5 — Merchant MID preview block extracted from GrantAccessModal.
// Renders the per-gateway list of MIDs the portal user will see, plus a
// warning banner for any incomplete MIDs (missing PG/LOB/partner). Pure
// presentational — parent provides the loaded data + the highlighted MID.

import { useMemo } from "react";
import { Badge } from "@/components/Shared";
import { Building2 } from "lucide-react";

export default function RelatedMidsPreview({
  loading,
  data,            // {by_gateway, total_mids, active_pgs} | {error: true} | null
  highlightMid,    // merchant_id string, optional
}) {
  // Phase 2.5 — useMemo so the JSX inline filter+flatMap doesn't re-run
  // on every parent render.
  const incompleteCount = useMemo(() => {
    if (!data || data.error) return 0;
    return (data.by_gateway || [])
      .flatMap((g) => g.mids)
      .filter((m) => !m.gateway || !m.lob || !m.partner_id)
      .length;
  }, [data]);

  return (
    <div className="border border-[var(--line-2)] bg-[var(--surface-2)]/40 rounded-sm" data-testid="related-mids-block">
      <div className="px-3 py-2 border-b border-[var(--line-2)] flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
          <Building2 size={11} />
          Portal will show these MIDs
        </div>
        {data && !data.error && (
          <div className="text-[11px] text-[var(--muted)]">
            <b className="text-[var(--ink)]">{data.total_mids}</b>{" "}
            MID{data.total_mids === 1 ? "" : "s"} across{" "}
            <b className="text-[var(--ink)]">{data.active_pgs.length}</b> PG{data.active_pgs.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
      <div className="px-3 py-2.5 max-h-44 overflow-auto" data-testid="related-mids-list">
        {loading && <div className="text-[11.5px] text-[var(--muted)]">Loading related MIDs…</div>}
        {!loading && data?.error && <div className="text-[11.5px] text-[var(--negative)]">Couldn't load related MIDs.</div>}
        {!loading && data && !data.error && data.total_mids === 0 && (
          <div className="text-[11.5px] text-[var(--muted)]">No merchants matched. The portal will be empty until you assign a MID.</div>
        )}
        {!loading && data && !data.error && data.total_mids > 0 && (
          <div className="space-y-2">
            {incompleteCount > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-sm px-2 py-1.5 text-[11px] text-amber-900 leading-snug" data-testid="incomplete-mids-warning">
                <b>{incompleteCount} MID{incompleteCount === 1 ? "" : "s"}</b> below {incompleteCount === 1 ? "is" : "are"} missing PG, LOB, or Partner —
                they'll show as <b>Unassigned</b> in the merchant's portal until you fill those in.
                Edit those rows from the Merchants page first.
              </div>
            )}
            {data.by_gateway.map((g) => (
              <div key={g.gateway}>
                <div className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">
                  {g.gateway} · <span className="text-[var(--ink)] tracking-normal">{g.mids.length}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {g.mids.map((m) => {
                    const incomplete = !m.gateway || !m.lob || !m.partner_id;
                    return (
                      <span
                        key={m.merchant_id}
                        className={`inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded-sm border ${
                          m.merchant_id === highlightMid
                            ? "bg-[var(--accent)]/15 border-[var(--accent)]/40 text-[var(--accent)] font-semibold"
                            : incomplete
                              ? "bg-amber-50 border-amber-300 text-amber-900"
                              : "bg-white border-[var(--line-2)] text-[var(--ink)]"
                        }`}
                        title={`${m.merchant_id}\nPG: ${m.gateway || "—"}\nLOB: ${m.lob || "—"}\nPartner: ${m.partner_id || "—"}\nStatus: ${m.status || "Active"}`}
                      >
                        <code className="font-mono text-[10.5px]">{m.merchant_id}</code>
                        {m.lob ? <span className="text-[var(--muted)]">{m.lob}</span> : <span className="italic text-amber-700">no LOB</span>}
                        {m.settlement_type && <Badge tone={m.settlement_type === "IS" ? "accent" : "muted"}>{m.settlement_type}</Badge>}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="text-[10.5px] text-[var(--muted)] mt-2 italic">
              The merchant's portal aggregates ALL of the above. Highlighted is the MID you clicked from. If any of these belong to a different real-world entity, edit the merchant_name BEFORE granting.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
